// Video compatibility utilities for Chrome and other browsers

/**
 * Check if video format is supported by the browser
 */
export function isVideoFormatSupported(type: string): boolean {
  const video = document.createElement('video');
  return video.canPlayType(type) !== '';
}

/**
 * Get the best video format for the current browser
 */
export function getBestVideoFormat(): string {
  if (isVideoFormatSupported('video/webm; codecs="vp9"')) {
    return 'webm';
  }
  if (isVideoFormatSupported('video/mp4; codecs="avc1.42E01E"')) {
    return 'mp4';
  }
  return 'mp4'; // fallback
}

/**
 * Add Safari-specific video optimization parameters
 */
export function addChromeVideoOptimizations(url: string): string {
  const urlObj = new URL(url);
  
  // Add Chrome-specific optimizations
  const params = new URLSearchParams(urlObj.search);
  
  // Safari caching quirks - prefer immutable cache for static video assets
  if (navigator.userAgent.includes('Safari') && !navigator.userAgent.includes('Chrome')) {
    // For Safari, add cache headers that work better with its caching system
    params.set('cache', 'immutable');
    params.set('max-age', '31536000'); // 1 year for static video assets
  }
  // Disable problematic caching for Chrome
  else if (navigator.userAgent.includes('Chrome')) {
    params.set('cache', 'false');
    params.set('v', Date.now().toString());
  }
  
  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * Check if we should use simplified video loading for compatibility
 */
export function shouldUseSimplifiedVideoLoading(): boolean {
  // Use simplified loading for older Chrome versions or if Cache API is problematic
  const isChrome = navigator.userAgent.includes('Chrome');
  const chromeVersion = isChrome ? parseInt(navigator.userAgent.match(/Chrome\/(\d+)/)?.[1] || '0') : 0;
  
  // Chrome versions < 80 had more aggressive caching issues
  return isChrome && chromeVersion < 80;
}

/**
 * Mobile-specific video optimizations
 */
export function isMobileDevice(): boolean {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
         (window.innerWidth <= 768 && 'ontouchstart' in window);
}

/**
 * Get mobile-optimized video parameters
 */
export function getMobileVideoParams(): Record<string, string> {
  if (!isMobileDevice()) return {};
  
  const isIOSDevice = isIOS();
  const isChromeAndroid = navigator.userAgent.includes('Chrome') && navigator.userAgent.includes('Android');
  
  if (isIOSDevice) {
    return {
      'br_300k': 'true', // Lower bitrate for iOS stability
      'q_auto:eco': 'true', // Eco quality for iOS
      'f_mp4': 'true', // MP4 format
      'vc_h264': 'true', // H.264 codec
      'ac_none': 'true', // Remove audio to reduce size
    };
  } else if (isChromeAndroid) {
    return {
      'br_500k': 'true', // Slightly higher bitrate for Chrome Android
      'q_auto:good': 'true', // Good quality for Chrome
      'f_mp4': 'true', // MP4 format
      'vc_h264': 'true', // H.264 codec
      'ac_aac': 'true', // AAC audio codec for Android
    };
  }
  
  // General mobile fallback
  return {
    'br_400k': 'true',
    'q_auto:low': 'true',
    'f_auto': 'true',
    'vc_h264': 'true',
  };
}

/**
 * Add mobile video optimizations to URL
 */
export function addMobileVideoOptimizations(url: string): string {
  if (!isMobileDevice()) return url;
  
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  // Add mobile-specific parameters
  const mobileParams = getMobileVideoParams();
  Object.entries(mobileParams).forEach(([key, value]) => {
    params.set(key, value);
  });
  
  // Add cache busting for mobile Chrome
  if (navigator.userAgent.includes('Chrome')) {
    params.set('cache_bust', Date.now().toString().slice(-6));
  }
  
  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * Check if device has limited bandwidth
 */
export function hasLimitedBandwidth(): boolean {
  // Check for save-data preference
  if ('connection' in navigator && (navigator as any).connection?.saveData) {
    return true;
  }
  
  // Check for slow connection
  if ('connection' in navigator) {
    const connection = (navigator as any).connection;
    return connection?.effectiveType === 'slow-2g' || 
           connection?.effectiveType === '2g' ||
           connection?.effectiveType === '3g';
  }
  
  return false;
}

/**
 * Browser detection utilities
 */
export function isSafari(): boolean {
  return /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
}

export function isFirefox(): boolean {
  return navigator.userAgent.includes('Firefox');
}

export function isIOS(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
}

/**
 * Generate HLS URL for iOS devices
 */
export function getHLSUrl(baseUrl: string): string {
  // Convert Cloudinary video URL to HLS format
  const hlsUrl = baseUrl.replace('/video/upload/', '/video/upload/f_auto,vc_auto:h264/').replace(/\.(mp4|webm|mov)$/, '.m3u8');
  return hlsUrl;
}

/**
 * Get the appropriate video URL based on device
 */
export function getOptimalVideoUrl(baseUrl: string): string {
  const isIOSDevice = isIOS();
  const isChromeAndroid = navigator.userAgent.includes('Chrome') && navigator.userAgent.includes('Android');
  let finalUrl = baseUrl;
  
  // For iOS, use optimized MP4 to avoid HLS flickering issues
  if (isIOSDevice) {
    const urlObj = new URL(baseUrl);
    const params = new URLSearchParams(urlObj.search);
    params.set('f_mp4', 'true'); // Force MP4 format
    params.set('vc_h264', 'true'); // H.264 codec for iOS
    params.set('br_300k', 'true'); // Lower bitrate for stability
    params.set('q_auto:eco', 'true'); // Eco quality for better iOS performance
    params.set('so_0', 'true'); // Start offset 0 for proper loading
    urlObj.search = params.toString();
    finalUrl = urlObj.toString();
  }
  // Chrome Android optimizations
  else if (isChromeAndroid) {
    const urlObj = new URL(baseUrl);
    const params = new URLSearchParams(urlObj.search);
    params.set('f_mp4', 'true'); // MP4 for better Chrome Android support
    params.set('vc_h264', 'true'); // H.264 works best on Android
    params.set('br_500k', 'true'); // Slightly higher bitrate than iOS
    params.set('q_auto:good', 'true'); // Good quality for Chrome Android
    urlObj.search = params.toString();
    finalUrl = urlObj.toString();
  }
  
  return finalUrl;
}

/**
 * Get browser-specific video optimizations
 */
export function getBrowserVideoOptimizations(url: string): string {
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  // Safari optimizations
  if (isSafari() || isIOS()) {
    // For iOS, we'll use HLS, so these params are for the HLS stream
    params.set('q_auto:good', 'true'); // Better quality for Safari
    
    // iOS Safari is very strict about video loading
    if (isIOS()) {
      params.set('br_300k', 'true'); // Lower bitrate for iOS
      params.set('so_0', 'true'); // Start offset 0 to ensure proper loading
    }
  }
  
  // Firefox optimizations
  else if (isFirefox()) {
    // Firefox has excellent WebM support
    params.set('f_webm', 'true');
    params.set('vc_vp9', 'true'); // VP9 codec works well
    params.set('q_auto:good', 'true');
  }
  
  // Chrome optimizations (already handled in addChromeVideoOptimizations)
  
  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * Safari Caching Quirks & Solutions:
 * 
 * Static Assets (videos, images):
 * - Use: Cache-Control: public, max-age=31536000, immutable
 * - Safari respects 'immutable' directive better than other browsers
 * 
 * API Lists/Dynamic Content:
 * - Use: Cache-Control: public, max-age=60, stale-while-revalidate=300
 * - Safari handles stale-while-revalidate more predictably
 */

/**
 * Get Safari-optimized cache headers for static media assets
 */
export function getSafariStaticCacheHeaders(): Record<string, string> {
  return {
    'Cache-Control': 'public, max-age=31536000, immutable',
    'Vary': 'Accept-Encoding'
  };
}

/**
 * Get Safari-optimized cache headers for API responses
 */
export function getSafariAPICacheHeaders(): Record<string, string> {
  return {
    'Cache-Control': 'public, max-age=60, stale-while-revalidate=300',
    'Vary': 'Accept-Encoding, Accept'
  };
}

/**
 * Apply Safari-specific caching optimizations to media URLs
 */
export function applySafariCaching(url: string, isStatic: boolean = true): string {
  if (!isSafari()) return url;
  
  const urlObj = new URL(url);
  const params = new URLSearchParams(urlObj.search);
  
  if (isStatic) {
    // For static assets like videos/images
    params.set('cache_control', 'public,max-age=31536000,immutable');
  } else {
    // For dynamic content/API responses  
    params.set('cache_control', 'public,max-age=60,stale-while-revalidate=300');
  }
  
  urlObj.search = params.toString();
  return urlObj.toString();
}

/**
 * iOS Video Requirements (confirmed):
 * 1. iOS blocks autoplay with audio - MUST use muted=true for autoplay
 * 2. playsinline=true avoids fullscreen lock - videos play inline instead of fullscreen
 * 3. webkit-playsinline=true provides legacy iOS support
 * 4. Autoplay is heavily restricted - better to disable entirely on iOS
 */

/**
 * Get browser-specific video attributes
 */
export function getBrowserVideoAttributes(): Record<string, any> {
  const attrs: Record<string, any> = {};
  
  // Safari/iOS specific attributes
  if (isSafari() || isIOS()) {
    attrs.playsInline = true; // Required for iOS - avoids fullscreen lock
    attrs['webkit-playsinline'] = 'true'; // String value for legacy iOS support
    attrs.autoplay = false; // iOS blocks autoplay aggressively
    attrs.preload = 'metadata'; // Conservative preloading
    attrs.muted = true; // iOS requires muted for any autoplay attempt
  }
  
  // Firefox specific
  if (isFirefox()) {
    attrs.preload = 'auto'; // Firefox handles preloading well
  }
  
  return attrs;
}